/**
 * Profile - the signed-in user's account page.
 *
 * Editable since account self-service landed: name and password. Email is
 * not editable here, because it is both an identity and the address we send
 * alerts to, so moving it needs a confirmation round trip to the new address
 * first. Role and plan are shown but belong to the business, not the reader.
 */

import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Divider, Button, Grid,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment, IconButton, Snackbar, Alert as MuiAlert,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useThemeMode, MONO, DISPLAY } from '../../context/ThemeContext';
import { useRecipes } from '../../theme/recipes';
import { fmtDate } from '../../utils/format';
import { updateProfile, changePassword } from '../../services/auth';
import CorrectEmailDialog from '../../components/CorrectEmailDialog';

const MIN_PASSWORD_LENGTH = 8;

function Profile() {
  const navigate = useNavigate();
  const { user, isAdmin, applyUserPatch } = useAuth();
  const { palette } = useThemeMode();
  const recipes = useRecipes();

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);

  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [pwError, setPwError] = useState('');
  const [savingPw, setSavingPw] = useState(false);

  const [fixingEmail, setFixingEmail] = useState(false);
  const [toast, setToast] = useState(null);

  // The address can only be corrected while it has never been confirmed. After
  // that it is an identity, and moving it needs a round trip to the new address
  // that does not exist yet.
  const emailUnconfirmed = user?.emailVerified === false;

  const fieldSx = { sx: recipes.controlSx };

  const saveName = async () => {
    const name = nameDraft.trim();
    if (!name || name === user?.name) { setEditingName(false); return; }
    setSavingName(true);
    try {
      const updated = await updateProfile(name);
      applyUserPatch({ name: updated.name });
      setEditingName(false);
      setToast({ severity: 'success', msg: 'Your name has been updated.' });
    } catch (e) {
      setToast({ severity: 'error', msg: e?.message || 'Could not save your name.' });
    } finally {
      setSavingName(false);
    }
  };

  const openPasswordDialog = () => {
    setPw({ current: '', next: '', confirm: '' });
    setPwError('');
    setPwOpen(true);
  };

  const savePassword = async () => {
    if (pw.next !== pw.confirm) { setPwError('The two new passwords do not match.'); return; }
    if (pw.next.length < MIN_PASSWORD_LENGTH) {
      setPwError(`Choose a password of at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    setSavingPw(true);
    setPwError('');
    try {
      await changePassword(pw.current, pw.next);
      setPwOpen(false);
      // Every other session was revoked server-side, so say so: someone who
      // is signed in on a phone should know why it asks again.
      setToast({
        severity: 'success',
        msg: 'Password changed. Any other device will need to sign in again.',
      });
    } catch (e) {
      setPwError(e?.message || 'Could not change your password.');
    } finally {
      setSavingPw(false);
    }
  };

  const initials = user?.name ? user.name.split(' ').map((p) => p[0]).slice(0, 2).join('') : 'FG';

  const rows = [
    { label: 'Email', value: user?.email || '-', mono: true, fixEmail: emailUnconfirmed },
    { label: 'Role', value: isAdmin ? 'Administrator' : 'Customer' },
    { label: 'Plan', value: isAdmin ? '-' : (user?.plan || 'Standard'), action: !isAdmin },
    { label: 'Status', value: (user?.status || 'active').toUpperCase(), chip: true },
    { label: 'Last login', value: user?.lastLogin ? fmtDate(user.lastLogin) : '-', mono: true },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Account"
        title="Profile"
        subtitle="Your account details on Farmgate"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Profile' }
        ]}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Box
                sx={{
                  width: 72, height: 72, mx: 'auto', mb: 2,
                  borderRadius: '50%',
                  bgcolor: isAdmin ? palette.warningBg : palette.accentBg,
                  color: isAdmin ? palette.warning : palette.accent,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '1.5rem',
                }}
              >
                {initials}
              </Box>
              {editingName ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                  <TextField
                    size="small"
                    autoFocus
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveName();
                      if (e.key === 'Escape') { setEditingName(false); setNameDraft(user?.name || ''); }
                    }}
                    inputProps={{ 'aria-label': 'Your name', maxLength: 80 }}
                    InputProps={fieldSx}
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Button size="small" onClick={saveName} disabled={savingName}
                      sx={{ textTransform: 'none', fontWeight: 700, color: palette.accent }}>
                      {savingName ? 'Saving…' : 'Save'}
                    </Button>
                    <Button size="small" onClick={() => { setEditingName(false); setNameDraft(user?.name || ''); }}
                      sx={{ textTransform: 'none', color: palette.textMuted }}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: '1.2rem', color: palette.text }}>
                    {user?.name || 'Guest'}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Edit your name"
                    onClick={() => { setNameDraft(user?.name || ''); setEditingName(true); }}
                    sx={{ color: palette.textMuted }}
                  >
                    <Icon icon="ph:pencil-simple" width={15} height={15} />
                  </IconButton>
                </Box>
              )}
              <Typography sx={{ fontSize: '0.85rem', color: palette.textSecondary, mb: 1.5 }}>
                {user?.email}
              </Typography>
              <Chip
                label={isAdmin ? 'ADMIN' : (user?.plan || 'Free').toUpperCase()}
                size="small"
                sx={{
                  height: 20, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em',
                  bgcolor: isAdmin ? palette.warningBg : palette.accentBg,
                  color: isAdmin ? palette.warning : palette.accent,
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${palette.divider}` }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Account details</Typography>
            </Box>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {rows.map((row, i) => (
                  <React.Fragment key={row.label}>
                    {i > 0 && <Divider sx={{ borderColor: palette.divider }} />}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '0.85rem', color: palette.textSecondary }}>{row.label}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {row.chip ? (
                          <Chip
                            label={row.value}
                            size="small"
                            sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em', bgcolor: palette.accentBg, color: palette.accent }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: palette.text, ...(row.mono ? { fontFamily: MONO } : {}) }}>
                            {row.value}
                          </Typography>
                        )}
                        {row.fixEmail && (
                          <Button
                            size="small"
                            onClick={() => setFixingEmail(true)}
                            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', color: palette.warning, '&:hover': { bgcolor: palette.warningBg } }}
                          >
                            Not confirmed. Fix it
                          </Button>
                        )}
                        {row.action && (
                          <Button
                            size="small"
                            onClick={() => navigate('/app/subscription')}
                            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', color: palette.accent, '&:hover': { bgcolor: palette.accentBg } }}
                          >
                            Change plan
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </React.Fragment>
                ))}
              </Box>

              <Divider sx={{ borderColor: palette.divider, my: 2.5 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: palette.text }}>
                    Password
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: palette.textSecondary, mt: 0.25 }}>
                    Changing it signs out every other device.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Icon icon="ph:key" width={15} height={15} />}
                  onClick={openPasswordDialog}
                  sx={{ textTransform: 'none', fontWeight: 600, borderColor: palette.cardBorder, color: palette.text }}
                >
                  Change password
                </Button>
              </Box>

              <Box
                sx={{
                  mt: 2.5, px: 1.75, py: 1.5,
                  borderRadius: '10px',
                  bgcolor: palette.surface,
                  border: `1px solid ${palette.cardBorder}`,
                  display: 'flex', gap: 1.25, alignItems: 'flex-start',
                }}
              >
                <Icon icon="ph:info" width={17} height={17} color={palette.textMuted} style={{ marginTop: 2, flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.78rem', color: palette.textSecondary, lineHeight: 1.55 }}>
                  {emailUnconfirmed
                    ? 'Your email address has not been confirmed yet, so you can still correct it above if it is wrong. Once it is confirmed it becomes how you sign in, and moving it needs a confirmation step we have not built. Your role and plan are set by Farmgate.'
                    : 'Your email address is confirmed, and it is both how you sign in and how we reach you about alerts, so moving it needs a confirmation step we have not built yet. Ask an administrator if it has to change. Your role and plan are set by Farmgate.'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <CorrectEmailDialog
        open={fixingEmail}
        onClose={() => setFixingEmail(false)}
        onCorrected={(addr) =>
          setToast({ severity: 'success', msg: `Changed. A confirmation link is on its way to ${addr}.` })}
      />

      {/* Change password */}
      <Dialog
        open={pwOpen}
        onClose={() => setPwOpen(false)}
        fullWidth
        PaperProps={{ sx: { ...recipes.dialogPaperProps.sx, maxWidth: 440, width: '100%' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>Change your password</DialogTitle>
        <DialogContent dividers>
          {pwError && <MuiAlert severity="error" sx={{ mb: 2 }}>{pwError}</MuiAlert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Current password"
              type={showPw ? 'text' : 'password'}
              size="small" fullWidth autoFocus autoComplete="current-password"
              value={pw.current}
              onChange={(e) => setPw((v) => ({ ...v, current: e.target.value }))}
              InputLabelProps={{ sx: recipes.labelSx }}
              InputProps={{
                sx: recipes.controlSx,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label={showPw ? 'Hide passwords' : 'Show passwords'}
                      onClick={() => setShowPw((v) => !v)}
                      sx={{ color: palette.textMuted }}
                    >
                      <Icon icon={showPw ? 'ph:eye-slash' : 'ph:eye'} width={16} height={16} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="New password"
              type={showPw ? 'text' : 'password'}
              size="small" fullWidth autoComplete="new-password"
              value={pw.next}
              onChange={(e) => setPw((v) => ({ ...v, next: e.target.value }))}
              helperText={`At least ${MIN_PASSWORD_LENGTH} characters`}
              InputLabelProps={{ sx: recipes.labelSx }}
              InputProps={{ sx: recipes.controlSx }}
            />
            <TextField
              label="Confirm new password"
              type={showPw ? 'text' : 'password'}
              size="small" fullWidth autoComplete="new-password"
              value={pw.confirm}
              onChange={(e) => setPw((v) => ({ ...v, confirm: e.target.value }))}
              error={pw.confirm.length > 0 && pw.confirm !== pw.next}
              helperText={pw.confirm.length > 0 && pw.confirm !== pw.next ? 'Both entries must match' : ' '}
              InputLabelProps={{ sx: recipes.labelSx }}
              InputProps={{ sx: recipes.controlSx }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPwOpen(false)} sx={{ color: palette.textMuted, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={savePassword}
            disabled={savingPw || !pw.current || pw.next.length < MIN_PASSWORD_LENGTH || pw.next !== pw.confirm}
            sx={{ ...recipes.primaryBtnSx, textTransform: 'none' }}
          >
            {savingPw ? 'Saving…' : 'Change password'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast && (
          <MuiAlert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </MuiAlert>
        )}
      </Snackbar>
    </Box>
  );
}

export default Profile;
